import React, { useCallback, useEffect, useRef, useState } from "react";
import useFetch from "hooks/useFetch";
import {
  Alert,
  Button,
  Card,
  Column,
  EmptyState,
  Icon,
  IconButton,
  Select,
  Skeleton,
  styles as ui,
  Table,
  Textarea,
  TextField,
  Toggle,
  useConfirm,
} from "stories/Admin";
import ContentModal from "../DuaPage/ContentModal";
import AyahAudioControls from "./AyahAudioControls";
import NorwegianSyncPanel from "./NorwegianSyncPanel";
import TashkeelSyncPanel from "./TashkeelSyncPanel";
import {
  isMissingTranslation,
  missingBySurah,
  NorwegianReport,
  NorwegianSurahSync,
} from "./norwegianSync";
import styles from "./QuranPage.module.scss";

interface QuranAyah {
  id: number;
  ayahNumber: number;
  arabicImmune?: string;
  arabicPlain?: string;
  /** Arabisk med full tashkeel (Tanzil), for Naskh-fonter. */
  arabicTashkeel?: string;
  translationNorwegian?: string;
  translationUrdu?: string;
  translationEnglish?: string;
  footnoteHtml?: string;
  audioUrlArabic?: string;
  audioUrlNorwegian?: string;
  audioUrlUrdu?: string;
  audioUrlEnglish?: string;
  fontFamilyArabic?: string;
  fontUrlArabic?: string;
  fontFamilyNorwegian?: string;
  fontUrlNorwegian?: string;
  fontFamilyUrdu?: string;
  fontUrlUrdu?: string;
  fontFamilyEnglish?: string;
  fontUrlEnglish?: string;
}

interface QuranSurah {
  id?: number;
  surahNumber: number;
  name: string;
  nameArabic?: string;
  verseCount?: number;
  revelationPlace?: string;
  introductionHtml?: string;
  juzId?: number;
  ayahs: QuranAyah[];
  /** Kildehenvisning for arabicTashkeel (Tanzil). */
  arabicTashkeelSource?: string | null;
}

interface QuranDashboard {
  surahCount: number;
  ayahCount: number;
  complete: boolean;
  missingSurahNumbers: number[];
  lastUpdatedAt: string | null;
}

interface QuranImportResult {
  success: boolean;
  importedSurahs: number;
  importedAyahs: number;
  skippedSurahs: number;
  failedSurahs: Array<{
    surahNumber: number;
    name: string;
    ayahCount: number;
    imported: boolean;
    message?: string;
  }>;
  completedAt: string;
}

interface QuranImportSurahResult {
  surahNumber: number;
  name: string;
  ayahCount: number;
  imported: boolean;
  message?: string;
}

interface QuranCleanupResult {
  scannedSurahs: number;
  scannedAyahs: number;
  updatedSurahs: number;
  updatedAyahs: number;
}

interface QuranTranslationImportResult {
  success: boolean;
  updatedSurahs: number;
  updatedAyahs: number;
  failedSurahs: Array<{
    surahNumber: number;
    name: string;
    message?: string;
  }>;
}

interface QuranFontImportResult {
  catalogFonts: number;
  hostedScriptFonts: number;
  updatedSurahs: number;
  updatedAyahs: number;
}

interface QuranFont {
  id: number;
  scriptKey?: string;
  name: string;
  fontFamily?: string;
  fontType: string;
  fontUrl?: string;
  hostedFontUrl?: string;
  fontFileName?: string;
}

const formatDateTime = (value: string | null): string => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("nb-NO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

const resolveFontHref = (font: QuranFont): string | undefined =>
  font.hostedFontUrl || font.fontUrl;

const SCRIPT_FONT_ORDER = [
  "indopak",
  "al_qalam_quran",
  "al_qalam_quran_majeed",
  "al_majeed_quranic",
  "al_mushaf",
];

/** Naskh-fonter viser arabicTashkeel (Tanzil, vanlig skrivemåte med full tashkeel). */
const NASKH_FONTS = [
  { key: "naskh_amiri", name: "Naskh – Amiri (med tashkeel)", family: "Amiri" },
  {
    key: "naskh_scheherazade",
    name: "Naskh – Scheherazade New (med tashkeel)",
    family: "Scheherazade New",
  },
];

export type RenderTop = (actions?: React.ReactNode) => React.ReactNode;

interface QuranTabProps {
  /** Renders the page header and tabs, with this tab's header actions. */
  top: RenderTop;
}

const QuranTextAdminTab: React.FC<QuranTabProps> = ({ top }) => {
  const { execute } = useFetch();
  const confirm = useConfirm();
  const executeRef = useRef(execute);
  executeRef.current = execute;

  const [dashboard, setDashboard] = useState<QuranDashboard | null>(null);
  const [surahs, setSurahs] = useState<QuranSurah[]>([]);
  const [selectedSurah, setSelectedSurah] = useState<QuranSurah | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingSurah, setLoadingSurah] = useState(false);
  const [importingAll, setImportingAll] = useState(false);
  const [importingSurah, setImportingSurah] = useState<number | null>(null);
  const [cleaning, setCleaning] = useState(false);
  const [importingTranslations, setImportingTranslations] = useState(false);
  const [importingAudio, setImportingAudio] = useState(false);
  const [importingFonts, setImportingFonts] = useState(false);
  const [fonts, setFonts] = useState<QuranFont[]>([]);
  const [arabicFontKey, setArabicFontKey] = useState("indopak");
  const [showUrdu, setShowUrdu] = useState(false);
  const [showEnglish, setShowEnglish] = useState(false);
  const [norwegianMissing, setNorwegianMissing] = useState<
    Record<number, number>
  >({});
  const [syncingNorwegian, setSyncingNorwegian] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [showSurahModal, setShowSurahModal] = useState(false);
  const [showAyahModal, setShowAyahModal] = useState(false);
  const [editingAyah, setEditingAyah] = useState<QuranAyah | null>(null);
  const [surahForm, setSurahForm] = useState({
    name: "",
    nameArabic: "",
    revelationPlace: "",
    introductionHtml: "",
  });
  const [ayahForm, setAyahForm] = useState({
    arabicImmune: "",
    arabicPlain: "",
    arabicTashkeel: "",
    translationNorwegian: "",
    translationUrdu: "",
    translationEnglish: "",
    footnoteHtml: "",
    audioUrlArabic: "",
    audioUrlNorwegian: "",
    audioUrlUrdu: "",
    audioUrlEnglish: "",
    fontFamilyArabic: "",
    fontUrlArabic: "",
    fontFamilyNorwegian: "",
    fontUrlNorwegian: "",
    fontFamilyUrdu: "",
    fontUrlUrdu: "",
    fontFamilyEnglish: "",
    fontUrlEnglish: "",
  });

  const fetchDashboard = useCallback(async () => {
    const result = (await executeRef.current(
      "/api/v1/admin/quran/dashboard",
      "GET",
    )) as QuranDashboard | null;
    if (result) setDashboard(result);
  }, []);

  const fetchSurahs = useCallback(async () => {
    const result = (await executeRef.current(
      "/api/v1/admin/quran/surahs",
      "GET",
    )) as QuranSurah[] | null;
    if (result) setSurahs(result);
  }, []);

  const fetchFonts = useCallback(async () => {
    const result = (await executeRef.current(
      "/api/v1/admin/quran/fonts",
      "GET",
    )) as QuranFont[] | null;
    if (result) setFonts(result);
  }, []);

  useEffect(() => {
    const styleId = "quran-font-preview-styles";
    let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    const css = fonts
      .map((font) => {
        const href = resolveFontHref(font);
        if (!href || !font.fontFamily) return "";
        const family = font.fontFamily.replace(/'/g, "\\'");
        return `@font-face{font-family:'${family}';src:url('${href}') format('truetype');font-display:swap;}`;
      })
      .join("\n");
    styleEl.textContent = css;
  }, [fonts]);

  const scriptFonts = SCRIPT_FONT_ORDER.map((key) =>
    fonts.find((font) => font.scriptKey === key),
  ).filter((font): font is QuranFont => Boolean(font));

  const selectedArabicFont =
    scriptFonts.find((font) => font.scriptKey === arabicFontKey) ??
    scriptFonts[0];
  const naskhFont = NASKH_FONTS.find((font) => font.key === arabicFontKey);
  const arabicFontFamily =
    naskhFont?.family ?? selectedArabicFont?.fontFamily ?? "_PDMS_IslamicFont";

  const refreshAll = useCallback(
    async (showLoading = true) => {
      if (showLoading) setLoading(true);
      setError(null);
      await Promise.all([fetchDashboard(), fetchSurahs(), fetchFonts()]);
      if (showLoading) setLoading(false);
    },
    [fetchDashboard, fetchSurahs, fetchFonts],
  );

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const loadSurah = async (surahNumber: number) => {
    setLoadingSurah(true);
    setError(null);
    const result = (await executeRef.current(
      `/api/v1/admin/quran/surahs/${surahNumber}`,
      "GET",
    )) as QuranSurah | null;
    if (result) {
      setSelectedSurah(result);
    } else {
      setError(`Kunne ikke hente surah ${surahNumber}.`);
    }
    setLoadingSurah(false);
  };

  const handleImportAll = async () => {
    if (
      !(await confirm({
        title: "Importere alle 114 surah-er?",
        message:
          "Teksten hentes fra leskoranen.no. Dette kan ta flere minutter.",
        confirmLabel: "Importer",
      }))
    ) {
      return;
    }
    setImportingAll(true);
    setError(null);
    setSuccess(null);
    const result = (await executeRef.current(
      "/api/v1/admin/quran/import?force=true",
      "POST",
    )) as QuranImportResult | null;
    if (result) {
      setSuccess(
        `Import ferdig: ${result.importedSurahs} surah-er, ${result.importedAyahs} vers. ` +
          `${result.skippedSurahs} hoppet over, ${result.failedSurahs.length} feilet.`,
      );
      await refreshAll(false);
      if (selectedSurah) {
        await loadSurah(selectedSurah.surahNumber);
      }
    } else {
      setError("Importen feilet. Sjekk loggene på serveren.");
    }
    setImportingAll(false);
  };

  const handleImportSurah = async (surahNumber: number) => {
    setImportingSurah(surahNumber);
    setError(null);
    setSuccess(null);
    const result = (await executeRef.current(
      `/api/v1/admin/quran/import/${surahNumber}?force=true`,
      "POST",
    )) as QuranImportSurahResult | null;
    if (result) {
      setSuccess(
        result.imported
          ? `Surah ${result.surahNumber} (${result.name}) importert med ${result.ayahCount} vers.`
          : `Surah ${result.surahNumber}: ${result.message ?? "ikke importert"}`,
      );
      await refreshAll(false);
      if (selectedSurah?.surahNumber === surahNumber) {
        await loadSurah(surahNumber);
      }
    } else {
      setError(`Kunne ikke importere surah ${surahNumber}.`);
    }
    setImportingSurah(null);
  };

  const handleCleanupAll = async () => {
    if (
      !(await confirm({
        title: "Rense HTML i all tekst?",
        message:
          "HTML-entiteter fjernes fra all arabisk tekst, norsk oversettelse og fotnoter.",
        confirmLabel: "Rens tekst",
      }))
    ) {
      return;
    }
    setCleaning(true);
    setError(null);
    setSuccess(null);
    const result = (await executeRef.current(
      "/api/v1/admin/quran/cleanup",
      "POST",
    )) as QuranCleanupResult | null;
    if (result) {
      setSuccess(
        `Opprydding ferdig: ${result.updatedAyahs} vers og ${result.updatedSurahs} surah-er oppdatert ` +
          `(av ${result.scannedAyahs} vers / ${result.scannedSurahs} surah-er).`,
      );
      await refreshAll(false);
      if (selectedSurah) {
        await loadSurah(selectedSurah.surahNumber);
      }
    } else {
      setError("Kunne ikke rense teksten. Sjekk loggene på serveren.");
    }
    setCleaning(false);
  };

  const handleImportTranslations = async () => {
    if (
      !(await confirm({
        title: "Hente urdu og engelsk?",
        message: "Oversettelsene hentes for alle importerte surah-er.",
        confirmLabel: "Hent oversettelser",
      }))
    ) {
      return;
    }
    setImportingTranslations(true);
    setError(null);
    setSuccess(null);
    const result = (await executeRef.current(
      "/api/v1/admin/quran/import-translations",
      "POST",
    )) as QuranTranslationImportResult | null;
    if (result) {
      setSuccess(
        `Oversettelser hentet: ${result.updatedAyahs} vers i ${result.updatedSurahs} surah-er oppdatert.` +
          (result.failedSurahs.length > 0
            ? ` ${result.failedSurahs.length} surah-er feilet.`
            : ""),
      );
      await refreshAll(false);
      if (selectedSurah) {
        await loadSurah(selectedSurah.surahNumber);
      }
    } else {
      setError("Kunne ikke hente oversettelser.");
    }
    setImportingTranslations(false);
  };

  const handleImportAudio = async () => {
    if (
      !(await confirm({
        title: "Hente lydfiler?",
        message:
          "Lenker til lyd (arabisk, norsk, urdu og engelsk) hentes for alle surah-er.",
        confirmLabel: "Hent lydfiler",
      }))
    ) {
      return;
    }
    setImportingAudio(true);
    setError(null);
    setSuccess(null);
    const result = (await executeRef.current(
      "/api/v1/admin/quran/import-audio",
      "POST",
    )) as QuranTranslationImportResult | null;
    if (result) {
      setSuccess(
        `Lydfiler hentet: ${result.updatedAyahs} vers i ${result.updatedSurahs} surah-er oppdatert.` +
          (result.failedSurahs.length > 0
            ? ` ${result.failedSurahs.length} surah-er feilet.`
            : ""),
      );
      await refreshAll(false);
      if (selectedSurah) {
        await loadSurah(selectedSurah.surahNumber);
      }
    } else {
      setError("Kunne ikke hente lydfiler.");
    }
    setImportingAudio(false);
  };

  const handleImportFonts = async () => {
    if (
      !(await confirm({
        title: "Hente fonter?",
        message: "Font-katalogen og font-informasjon hentes for alle surah-er.",
        confirmLabel: "Hent fonter",
      }))
    ) {
      return;
    }
    setImportingFonts(true);
    setError(null);
    setSuccess(null);
    const result = (await executeRef.current(
      "/api/v1/admin/quran/import-fonts",
      "POST",
    )) as QuranFontImportResult | null;
    if (result) {
      setSuccess(
        `Fonter hentet: ${result.hostedScriptFonts} script-fonter hostet, ${result.catalogFonts} i katalog, ${result.updatedAyahs} vers oppdatert i ${result.updatedSurahs} surah-er.`,
      );
      await refreshAll(false);
      if (selectedSurah) {
        await loadSurah(selectedSurah.surahNumber);
      }
    } else {
      setError("Kunne ikke hente fonter.");
    }
    setImportingFonts(false);
  };

  const handleImportSurahTranslations = async (surahNumber: number) => {
    setImportingSurah(surahNumber);
    setError(null);
    setSuccess(null);
    const result = (await executeRef.current(
      `/api/v1/admin/quran/import-translations/${surahNumber}`,
      "POST",
    )) as QuranImportSurahResult | null;
    if (result) {
      setSuccess(result.message ?? `Surah ${surahNumber} oppdatert.`);
      if (selectedSurah?.surahNumber === surahNumber) {
        await loadSurah(surahNumber);
      }
    } else {
      setError(`Kunne ikke hente oversettelser for surah ${surahNumber}.`);
    }
    setImportingSurah(null);
  };

  const handleSyncSurahNorwegian = async (surahNumber: number) => {
    setSyncingNorwegian(surahNumber);
    setError(null);
    setSuccess(null);
    const result = (await executeRef.current(
      `/api/v1/admin/quran/norwegian/sync/${surahNumber}`,
      "POST",
    )) as NorwegianSurahSync | null;
    if (result) {
      setSuccess(
        `Surah ${result.surahNumber} (${result.name}): ` +
          (result.missingBefore === 0
            ? "ingen vers mangler norsk oversettelse."
            : `${result.updatedAyahs} av ${result.missingBefore} vers fikk norsk oversettelse` +
              (result.stillMissingAyahs.length
                ? `, ${result.stillMissingAyahs.length} mangler fortsatt.`
                : ".")),
      );
      setNorwegianMissing((prev) => {
        const next = { ...prev };
        if (result.stillMissingAyahs.length) {
          next[surahNumber] = result.stillMissingAyahs.length;
        } else {
          delete next[surahNumber];
        }
        return next;
      });
      if (selectedSurah?.surahNumber === surahNumber) {
        await loadSurah(surahNumber);
      }
    } else {
      setError(`Kunne ikke hente norsk oversettelse for surah ${surahNumber}.`);
    }
    setSyncingNorwegian(null);
  };

  const openSurahEdit = (surah: QuranSurah) => {
    setSurahForm({
      name: surah.name,
      nameArabic: surah.nameArabic ?? "",
      revelationPlace: surah.revelationPlace ?? "",
      introductionHtml: surah.introductionHtml ?? "",
    });
    setShowSurahModal(true);
  };

  const saveSurah = async () => {
    if (!selectedSurah) return;
    const result = (await executeRef.current(
      `/api/v1/admin/quran/surahs/${selectedSurah.surahNumber}`,
      "PUT",
      surahForm,
    )) as QuranSurah | null;
    if (result) {
      setSelectedSurah(result);
      setShowSurahModal(false);
      setSuccess(`Surah ${result.surahNumber} oppdatert.`);
      await fetchSurahs();
    } else {
      setError("Kunne ikke lagre surah.");
    }
  };

  const deleteSurah = async (surahNumber: number) => {
    if (
      !(await confirm({
        title: `Slette surah ${surahNumber}?`,
        message: "Surahen og alle versene slettes. Dette kan ikke angres.",
        confirmLabel: "Slett surah",
        tone: "danger",
      }))
    ) {
      return;
    }
    await executeRef.current(
      `/api/v1/admin/quran/surahs/${surahNumber}`,
      "DELETE",
    );
    setSuccess(`Surah ${surahNumber} slettet.`);
    if (selectedSurah?.surahNumber === surahNumber) {
      setSelectedSurah(null);
    }
    await refreshAll(false);
  };

  const openAyahEdit = (ayah: QuranAyah) => {
    setEditingAyah(ayah);
    setAyahForm({
      arabicImmune: ayah.arabicImmune ?? "",
      arabicPlain: ayah.arabicPlain ?? "",
      arabicTashkeel: ayah.arabicTashkeel ?? "",
      translationNorwegian: ayah.translationNorwegian ?? "",
      translationUrdu: ayah.translationUrdu ?? "",
      translationEnglish: ayah.translationEnglish ?? "",
      footnoteHtml: ayah.footnoteHtml ?? "",
      audioUrlArabic: ayah.audioUrlArabic ?? "",
      audioUrlNorwegian: ayah.audioUrlNorwegian ?? "",
      audioUrlUrdu: ayah.audioUrlUrdu ?? "",
      audioUrlEnglish: ayah.audioUrlEnglish ?? "",
      fontFamilyArabic: ayah.fontFamilyArabic ?? "",
      fontUrlArabic: ayah.fontUrlArabic ?? "",
      fontFamilyNorwegian: ayah.fontFamilyNorwegian ?? "",
      fontUrlNorwegian: ayah.fontUrlNorwegian ?? "",
      fontFamilyUrdu: ayah.fontFamilyUrdu ?? "",
      fontUrlUrdu: ayah.fontUrlUrdu ?? "",
      fontFamilyEnglish: ayah.fontFamilyEnglish ?? "",
      fontUrlEnglish: ayah.fontUrlEnglish ?? "",
    });
    setShowAyahModal(true);
  };

  const saveAyah = async () => {
    if (!editingAyah?.id || !selectedSurah) return;
    const result = (await executeRef.current(
      `/api/v1/admin/quran/ayahs/${editingAyah.id}`,
      "PUT",
      ayahForm,
    )) as QuranAyah | null;
    if (result) {
      setShowAyahModal(false);
      setEditingAyah(null);
      setSuccess(`Vers ${result.ayahNumber} oppdatert.`);
      await loadSurah(selectedSurah.surahNumber);
    } else {
      setError("Kunne ikke lagre vers.");
    }
  };

  const deleteAyah = async (ayah: QuranAyah) => {
    if (!ayah.id || !selectedSurah) return;
    if (
      !(await confirm({
        title: `Slette vers ${ayah.ayahNumber}?`,
        message: "Verset slettes. Dette kan ikke angres.",
        confirmLabel: "Slett vers",
        tone: "danger",
      }))
    ) {
      return;
    }
    await executeRef.current(`/api/v1/admin/quran/ayahs/${ayah.id}`, "DELETE");
    setSuccess(`Vers ${ayah.ayahNumber} slettet.`);
    await loadSurah(selectedSurah.surahNumber);
    await fetchDashboard();
  };

  const baseBusy =
    importingAll || cleaning || importingTranslations || importingAudio;
  const surahBusy = selectedSurah
    ? importingSurah === selectedSurah.surahNumber
    : false;

  if (loading) {
    return (
      <>
        {top(
          <Button
            variant="secondary"
            disabled
            icon={<Icon name="swap" size={16} strokeWidth={2} />}
          >
            Oppdater
          </Button>,
        )}
        <div className={ui.g4}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={styles.stat}>
              <Skeleton width="60%" height={26} />
              <Skeleton width="40%" height={13} />
            </div>
          ))}
        </div>
        <Card>
          <Skeleton width="30%" height={20} />
          <Skeleton height={40} />
        </Card>
      </>
    );
  }

  const lastUpdated = formatDateTime(dashboard?.lastUpdatedAt ?? null).split(
    ", ",
  );

  const arabicFor = (ayah: QuranAyah) =>
    (naskhFont
      ? (ayah.arabicTashkeel ?? ayah.arabicPlain ?? ayah.arabicImmune)
      : arabicFontKey === "indopak"
        ? (ayah.arabicImmune ?? ayah.arabicPlain)
        : (ayah.arabicPlain ?? ayah.arabicImmune)) ?? "—";

  const ayahColumns: Array<Column<QuranAyah>> = [
    {
      key: "number",
      header: "#",
      width: 48,
      numeric: true,
      render: (ayah) => <b>{ayah.ayahNumber}</b>,
    },
    {
      key: "arabic",
      header: "Arabisk",
      render: (ayah) => (
        <>
          <span
            dir="rtl"
            lang="ar"
            className={styles.arabicCell}
            style={{ fontFamily: `'${arabicFontFamily}'` }}
            dangerouslySetInnerHTML={{ __html: arabicFor(ayah) }}
          />
          {naskhFont && !ayah.arabicTashkeel && (
            <span className={styles.missingTranslation}>Mangler tashkeel</span>
          )}
        </>
      ),
    },
    {
      key: "norwegian",
      header: "Norsk",
      render: (ayah) =>
        isMissingTranslation(ayah.translationNorwegian) ? (
          <span className={styles.missingTranslation}>Mangler</span>
        ) : (
          <span
            className={styles.translationCell}
            dangerouslySetInnerHTML={{
              __html: ayah.translationNorwegian ?? "",
            }}
          />
        ),
    },
    ...(showUrdu
      ? [
          {
            key: "urdu",
            header: "Urdu",
            render: (ayah: QuranAyah) => (
              <span
                dir="rtl"
                className={styles.urduCell}
                dangerouslySetInnerHTML={{
                  __html: ayah.translationUrdu ?? "—",
                }}
              />
            ),
          },
        ]
      : []),
    ...(showEnglish
      ? [
          {
            key: "english",
            header: "Engelsk",
            render: (ayah: QuranAyah) => (
              <span
                className={styles.translationCell}
                dangerouslySetInnerHTML={{
                  __html: ayah.translationEnglish ?? "—",
                }}
              />
            ),
          },
        ]
      : []),
    {
      key: "audio",
      header: "Lyd",
      render: (ayah) => <AyahAudioControls ayah={ayah} compact />,
    },
    {
      key: "actions",
      header: <span className={ui.srOnly}>Handlinger</span>,
      align: "right",
      render: (ayah) => (
        <span className={styles.rowActions}>
          <IconButton
            label={`Rediger vers ${ayah.ayahNumber}`}
            icon={<Icon name="edit" size={17} strokeWidth={1.9} />}
            onClick={() => openAyahEdit(ayah)}
          />
          <IconButton
            label={`Slett vers ${ayah.ayahNumber}`}
            icon={<Icon name="trash" size={17} strokeWidth={1.9} />}
            onClick={() => deleteAyah(ayah)}
          />
        </span>
      ),
    },
  ];

  const audioFields: Array<{
    key:
      | "audioUrlArabic"
      | "audioUrlNorwegian"
      | "audioUrlUrdu"
      | "audioUrlEnglish";
    label: string;
  }> = [
    { key: "audioUrlArabic", label: "Lyd-URL arabisk" },
    { key: "audioUrlNorwegian", label: "Lyd-URL norsk" },
    { key: "audioUrlUrdu", label: "Lyd-URL urdu" },
    { key: "audioUrlEnglish", label: "Lyd-URL engelsk" },
  ];

  return (
    <>
      {top(
        <Button
          variant="secondary"
          onClick={() => refreshAll(false)}
          disabled={baseBusy}
          icon={
            baseBusy ? (
              <span className={ui.spinner} aria-hidden="true" />
            ) : (
              <Icon name="swap" size={16} strokeWidth={2} />
            )
          }
        >
          Oppdater
        </Button>,
      )}

      {error && (
        <Alert tone="error">
          {error}{" "}
          <button
            type="button"
            className={ui.linkBtn}
            onClick={() => setError(null)}
          >
            Lukk
          </button>
        </Alert>
      )}
      {success && (
        <Alert tone="ok">
          {success}{" "}
          <button
            type="button"
            className={ui.linkBtn}
            onClick={() => setSuccess(null)}
          >
            Lukk
          </button>
        </Alert>
      )}

      {dashboard && (
        <div className={ui.g4}>
          <div className={styles.stat}>
            <b>{dashboard.surahCount} / 114</b>
            <span>Surah-er</span>
          </div>
          <div className={styles.stat}>
            <b>{dashboard.ayahCount.toLocaleString("nb-NO")}</b>
            <span>Vers</span>
          </div>
          <div className={styles.stat}>
            <b>
              {dashboard.complete ? (
                "Komplett"
              ) : (
                <span className={styles.statWarn}>Ufullstendig</span>
              )}
            </b>
            <span>Status</span>
          </div>
          <div className={styles.stat}>
            <b>{lastUpdated[0]}</b>
            <span>
              Sist oppdatert{lastUpdated[1] ? ` kl. ${lastUpdated[1]}` : ""}
            </span>
          </div>
        </div>
      )}

      {dashboard && dashboard.missingSurahNumbers.length > 0 && (
        <Alert tone="warn">
          Mangler surah-nummer: {dashboard.missingSurahNumbers.join(", ")}
        </Alert>
      )}

      <Card title="Import og vedlikehold">
        <div className={styles.buttonRow}>
          <Button
            onClick={handleImportAll}
            disabled={baseBusy}
            loading={importingAll}
            icon={<Icon name="download" size={16} strokeWidth={2} />}
          >
            {importingAll ? "Importerer alle …" : "Importer alle 114"}
          </Button>
          <Button
            variant="secondary"
            onClick={handleImportTranslations}
            disabled={baseBusy}
            loading={importingTranslations}
            icon={<Icon name="book" size={16} strokeWidth={2} />}
          >
            {importingTranslations
              ? "Henter urdu og engelsk …"
              : "Hent urdu og engelsk"}
          </Button>
          <Button
            variant="secondary"
            onClick={handleImportAudio}
            disabled={baseBusy || importingFonts}
            loading={importingAudio}
            icon={<Icon name="download" size={16} strokeWidth={2} />}
          >
            {importingAudio ? "Henter lydfiler …" : "Hent lydfiler"}
          </Button>
          <Button
            variant="secondary"
            onClick={handleImportFonts}
            disabled={baseBusy || importingFonts}
            loading={importingFonts}
            icon={<Icon name="doc" size={16} strokeWidth={2} />}
          >
            {importingFonts ? "Henter fonter …" : "Hent fonter"}
          </Button>
          <Button
            variant="secondary"
            onClick={handleCleanupAll}
            disabled={baseBusy}
            loading={cleaning}
            icon={<Icon name="wand" size={16} strokeWidth={2} />}
          >
            {cleaning ? "Renser tekst …" : "Rens HTML i all tekst"}
          </Button>
        </div>
        <span className={ui.help}>
          Alle handlingene ber om bekreftelse først. Full import kan ta flere
          minutter.
        </span>
      </Card>

      <NorwegianSyncPanel
        onReport={(report: NorwegianReport) =>
          setNorwegianMissing(missingBySurah(report))
        }
        onFinished={() => {
          if (selectedSurah) loadSurah(selectedSurah.surahNumber);
        }}
      />

      <TashkeelSyncPanel
        onFinished={() => {
          if (selectedSurah) loadSurah(selectedSurah.surahNumber);
        }}
      />

      <div className={styles.columns}>
        <Card
          title={`Surah-er (${surahs.length})`}
          className={styles.surahListCard}
        >
          {surahs.length === 0 ? (
            <EmptyState icon="book" title="Ingen surah-er">
              Ingen surah-er er importert ennå.
            </EmptyState>
          ) : (
            <div className={styles.surahList}>
              {surahs.map((surah) => {
                const on = selectedSurah?.surahNumber === surah.surahNumber;
                return (
                  <button
                    key={surah.surahNumber}
                    type="button"
                    aria-current={on ? "true" : undefined}
                    className={`${styles.surahItem} ${on ? styles.surahItemOn : ""}`}
                    onClick={() => loadSurah(surah.surahNumber)}
                  >
                    <span className={styles.surahNumber}>
                      {surah.surahNumber}
                    </span>
                    <b className={styles.surahName}>{surah.name}</b>
                    {norwegianMissing[surah.surahNumber] ? (
                      <span
                        className={styles.surahMissing}
                        title={`${norwegianMissing[surah.surahNumber]} vers mangler norsk oversettelse`}
                      >
                        NO {norwegianMissing[surah.surahNumber]}
                      </span>
                    ) : null}
                    <span className={ui.help}>{surah.verseCount ?? "?"} v</span>
                  </button>
                );
              })}
            </div>
          )}
        </Card>

        <Card
          title={
            selectedSurah ? (
              <>
                {selectedSurah.surahNumber}. {selectedSurah.name}
                {selectedSurah.nameArabic && (
                  <span
                    className={styles.surahArabicName}
                    lang="ar"
                    style={{ fontFamily: `'${arabicFontFamily}'` }}
                  >
                    {selectedSurah.nameArabic}
                  </span>
                )}
              </>
            ) : (
              "Velg en surah"
            )
          }
          actions={
            selectedSurah && (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    handleImportSurahTranslations(selectedSurah.surahNumber)
                  }
                  disabled={surahBusy}
                  icon={<Icon name="book" size={14} strokeWidth={2} />}
                >
                  Urdu/engelsk
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    handleSyncSurahNorwegian(selectedSurah.surahNumber)
                  }
                  disabled={surahBusy || syncingNorwegian !== null}
                  loading={syncingNorwegian === selectedSurah.surahNumber}
                  icon={<Icon name="book" size={14} strokeWidth={2} />}
                >
                  Hent norsk
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => handleImportSurah(selectedSurah.surahNumber)}
                  disabled={surahBusy}
                  loading={surahBusy}
                  icon={<Icon name="download" size={14} strokeWidth={2} />}
                >
                  Hent på nytt
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => openSurahEdit(selectedSurah)}
                  icon={<Icon name="edit" size={14} strokeWidth={2} />}
                >
                  Rediger
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => deleteSurah(selectedSurah.surahNumber)}
                  icon={<Icon name="trash" size={14} strokeWidth={2} />}
                >
                  Slett
                </Button>
              </>
            )
          }
          className={styles.detailCard}
        >
          {loadingSurah ? (
            <>
              <Skeleton height={42} />
              <Skeleton height={28} />
              <Skeleton height={28} />
              <Skeleton height={28} />
            </>
          ) : selectedSurah ? (
            <>
              {selectedSurah.introductionHtml && (
                <div
                  className={styles.introHtml}
                  dangerouslySetInnerHTML={{
                    __html: selectedSurah.introductionHtml,
                  }}
                />
              )}

              <div className={styles.toolbar}>
                <Select
                  aria-label="Arabisk font"
                  fieldClassName={styles.fontSelect}
                  value={arabicFontKey}
                  onChange={(e) => setArabicFontKey(e.target.value)}
                >
                  {scriptFonts.length > 0
                    ? scriptFonts.map((font) => (
                        <option key={font.scriptKey} value={font.scriptKey}>
                          Arabisk font: {font.name}
                        </option>
                      ))
                    : SCRIPT_FONT_ORDER.map((key) => (
                        <option key={key} value={key}>
                          Arabisk font: {key}
                        </option>
                      ))}
                  {NASKH_FONTS.map((font) => (
                    <option key={font.key} value={font.key}>
                      Arabisk font: {font.name}
                    </option>
                  ))}
                </Select>
                <span
                  className={styles.fontPreview}
                  lang="ar"
                  style={{ fontFamily: `'${arabicFontFamily}'` }}
                >
                  {naskhFont ? "بِسْمِ اللَّهِ" : "بِسْمِ اللّٰهِ"}
                </span>
                <span className={styles.toggles}>
                  <Toggle
                    label="Urdu"
                    checked={showUrdu}
                    onChange={() => setShowUrdu((value) => !value)}
                  />
                  <Toggle
                    label="Engelsk"
                    checked={showEnglish}
                    onChange={() => setShowEnglish((value) => !value)}
                  />
                </span>
              </div>

              <Table
                columns={ayahColumns}
                rows={selectedSurah.ayahs}
                rowKey={(ayah) => ayah.id ?? ayah.ayahNumber}
                caption={`Vers i surah ${selectedSurah.name}`}
                empty={
                  <EmptyState icon="book" title="Ingen vers">
                    Denne surahen har ingen vers ennå.
                  </EmptyState>
                }
              />
              {naskhFont && selectedSurah.arabicTashkeelSource && (
                <span className={ui.help}>
                  {selectedSurah.arabicTashkeelSource}
                </span>
              )}
            </>
          ) : (
            <EmptyState icon="book" title="Ingen surah valgt">
              Velg en surah i listen for å se og redigere versene.
            </EmptyState>
          )}
        </Card>
      </div>

      {fonts.length > 0 && (
        <Card title={`Font-katalog (${fonts.length})`}>
          <div className={styles.fontCatalog}>
            {fonts.map((font) => {
              const href = resolveFontHref(font);
              return (
                <div key={font.id} className={styles.fontItem}>
                  <div className={styles.fontItemHead}>
                    <b>{font.name}</b>
                    {href && (
                      <a
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.link}
                      >
                        Last ned .ttf
                      </a>
                    )}
                  </div>
                  <span className={ui.help}>
                    {font.fontFamily} · {font.fontType}
                    {font.scriptKey ? ` · ${font.scriptKey}` : ""}
                  </span>
                  {font.fontFamily && (
                    <span
                      className={styles.fontSample}
                      lang="ar"
                      style={{ fontFamily: `'${font.fontFamily}'` }}
                    >
                      بِسْمِ اللّٰهِ الرَّحْمٰنِ الرَّحِيْمِ
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <ContentModal
        open={showSurahModal}
        title="Rediger surah"
        icon="book"
        saveLabel="Lagre"
        onSave={saveSurah}
        onClose={() => setShowSurahModal(false)}
      >
        <TextField
          label="Navn"
          value={surahForm.name}
          onChange={(e) => setSurahForm({ ...surahForm, name: e.target.value })}
        />
        <TextField
          label="Arabisk navn"
          value={surahForm.nameArabic}
          onChange={(e) =>
            setSurahForm({ ...surahForm, nameArabic: e.target.value })
          }
          dir="rtl"
          lang="ar"
          className={styles.arabicInput}
        />
        <TextField
          label="Åpenbaringssted"
          value={surahForm.revelationPlace}
          onChange={(e) =>
            setSurahForm({ ...surahForm, revelationPlace: e.target.value })
          }
        />
        <Textarea
          label="Introduksjon (HTML)"
          rows={6}
          value={surahForm.introductionHtml}
          onChange={(e) =>
            setSurahForm({ ...surahForm, introductionHtml: e.target.value })
          }
        />
      </ContentModal>

      <ContentModal
        open={showAyahModal}
        title={`Rediger vers ${editingAyah?.ayahNumber ?? ""}`}
        icon="quote"
        saveLabel="Lagre"
        onSave={saveAyah}
        onClose={() => setShowAyahModal(false)}
      >
        <Textarea
          label="Arabisk (med diakritiske tegn)"
          rows={3}
          value={ayahForm.arabicImmune}
          onChange={(e) =>
            setAyahForm({ ...ayahForm, arabicImmune: e.target.value })
          }
          dir="rtl"
          lang="ar"
          className={styles.arabicInput}
        />
        <Textarea
          label="Arabisk (uten diakritiske tegn)"
          rows={3}
          value={ayahForm.arabicPlain}
          onChange={(e) =>
            setAyahForm({ ...ayahForm, arabicPlain: e.target.value })
          }
          dir="rtl"
          lang="ar"
          className={styles.arabicInput}
        />
        <Textarea
          label="Arabisk med tashkeel (Naskh, Tanzil)"
          rows={3}
          value={ayahForm.arabicTashkeel}
          onChange={(e) =>
            setAyahForm({ ...ayahForm, arabicTashkeel: e.target.value })
          }
          dir="rtl"
          lang="ar"
          className={styles.arabicInput}
          style={{ fontFamily: "'Amiri'" }}
        />
        <Textarea
          label="Norsk oversettelse"
          rows={4}
          value={ayahForm.translationNorwegian}
          onChange={(e) =>
            setAyahForm({ ...ayahForm, translationNorwegian: e.target.value })
          }
        />
        <Textarea
          label="Urdu-oversettelse"
          rows={4}
          value={ayahForm.translationUrdu}
          onChange={(e) =>
            setAyahForm({ ...ayahForm, translationUrdu: e.target.value })
          }
          dir="rtl"
        />
        <Textarea
          label="Engelsk oversettelse"
          rows={4}
          value={ayahForm.translationEnglish}
          onChange={(e) =>
            setAyahForm({ ...ayahForm, translationEnglish: e.target.value })
          }
        />
        {audioFields.map(({ key, label }) => (
          <div key={key} className={styles.audioField}>
            <TextField
              label={label}
              value={ayahForm[key]}
              onChange={(e) =>
                setAyahForm({ ...ayahForm, [key]: e.target.value })
              }
            />
            {ayahForm[key] && (
              <audio
                controls
                preload="none"
                src={ayahForm[key]}
                className={styles.audioPlayer}
              >
                Nettleseren din støtter ikke lydavspilling.
              </audio>
            )}
          </div>
        ))}
        <Textarea
          label="Fotnote (HTML)"
          rows={3}
          value={ayahForm.footnoteHtml}
          onChange={(e) =>
            setAyahForm({ ...ayahForm, footnoteHtml: e.target.value })
          }
        />
        <div>
          <span className={ui.lbl}>Fonter</span>
          <div className={styles.fontPreviewGrid}>
            {[
              {
                label: "Arabisk",
                family: ayahForm.fontFamilyArabic,
                url: ayahForm.fontUrlArabic,
              },
              {
                label: "Norsk",
                family: ayahForm.fontFamilyNorwegian,
                url: ayahForm.fontUrlNorwegian,
              },
              {
                label: "Urdu",
                family: ayahForm.fontFamilyUrdu,
                url: ayahForm.fontUrlUrdu,
              },
              {
                label: "Engelsk",
                family: ayahForm.fontFamilyEnglish,
                url: ayahForm.fontUrlEnglish,
              },
            ].map((font) => (
              <div key={font.label} className={styles.fontPreviewItem}>
                <b>{font.label}</b>
                <span>{font.family || "—"}</span>
                {font.url ? (
                  <a
                    href={font.url}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.link}
                  >
                    Last ned font
                  </a>
                ) : (
                  <span className={ui.help}>Ingen font-URL</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </ContentModal>
    </>
  );
};

export default QuranTextAdminTab;
